import { api } from "@/integrations/clients/carAuction/carAuctionClient";
import { MediaService } from "@/services/media/mediaService";
import { compressImageForPreview } from "@/utils/imageCompression";
import type {
  DealerPortalCompany,
  DealerPortalCustomer,
  DealerPortalCustomerDocument,
  DealerPortalCustomerHistoryEvent,
  DealerPortalCustomerJourney,
  DealerPortalCustomerNote,
  DealerPortalCustomerType,
  DealerPortalCustomerVehicleConnection,
  DealerPortalVehicle,
  DealerPortalVehicleDocument,
  DealerPortalVehicleServiceDetail,
  DealerPortalVehicleStatus,
  DealerPortalColleague,
  DealerPortalVehicleCarTestSummary,
} from "@/types/dealerPortal";

const BASE = "/dealer-portal";

type ApiResponse<T> = { success: boolean; data: T };

function unwrap<T>(p: Promise<ApiResponse<T>>): Promise<T> {
  return p.then((r) => (r as ApiResponse<T>).data);
}

export const dealerPortalService = {
  getCompany: () => unwrap(api.get<ApiResponse<DealerPortalCompany>>(`${BASE}/company`)),

  updateCompanyLogo: (body: { logoPreviewMediaId?: string | null; logoFullSizeMediaId?: string | null }) =>
    unwrap(api.patch<ApiResponse<DealerPortalCompany>>(`${BASE}/company/logo`, body)),

  getColleagues: () => unwrap(api.get<ApiResponse<DealerPortalColleague[]>>(`${BASE}/colleagues`)),

  getCustomers: (search?: string) =>
    unwrap(
      api.get<ApiResponse<DealerPortalCustomer[]>>(
        `${BASE}/customers${search != null && search !== "" ? `?search=${encodeURIComponent(search)}` : ""}`
      )
    ),

  getCustomersPaginated: (
    params: { search?: string; page: number; limit: number; role?: "buyer" | "seller"; customerType?: "PRIVATE" | "BUSINESS" | "DEALER" }
  ): Promise<{ items: DealerPortalCustomer[]; total: number; page: number; limit: number }> => {
    const q = new URLSearchParams();
    if (params.search != null && params.search !== "") q.set("search", params.search);
    if (params.role === "buyer" || params.role === "seller") q.set("role", params.role);
    if (params.customerType != null && params.customerType !== "") q.set("customerType", params.customerType);
    q.set("page", String(params.page));
    q.set("limit", String(params.limit));
    return unwrap(
      api.get<ApiResponse<{ items: DealerPortalCustomer[]; total: number; page: number; limit: number }>>(
        `${BASE}/customers?${q.toString()}`
      )
    );
  },

  createCustomer: (body: {
    name: string;
    email?: string;
    phone?: string;
    customerType?: DealerPortalCustomerType | "private" | "business" | "dealer";
    personalNumber?: string;
    street?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    companyConnection?: string;
    orgNumber?: string;
    vatNumber?: string;
    contactPersonName?: string;
    contactPersonEmail?: string;
    contactPersonPhone?: string;
    isBuyer?: boolean;
    isSeller?: boolean;
  }) =>
    unwrap(api.post<ApiResponse<DealerPortalCustomer>>(`${BASE}/customers`, body)),

  getCustomer: (id: string) =>
    unwrap(
      api.get<
        ApiResponse<
          DealerPortalCustomer & {
            journeys?: DealerPortalCustomerJourney[];
            vehicleConnections?: DealerPortalCustomerVehicleConnection[];
            documents?: DealerPortalCustomerDocument[];
            historyEvents?: DealerPortalCustomerHistoryEvent[];
            notes?: DealerPortalCustomerNote[];
          }
        >
      >(`${BASE}/customers/${id}`)
    ),

  updateCustomer: (
    id: string,
    body: {
      name?: string;
      email?: string;
      phone?: string;
      customerType?: DealerPortalCustomerType | "private" | "business" | "dealer";
      personalNumber?: string;
      street?: string;
      postalCode?: string;
      city?: string;
      country?: string;
      companyConnection?: string;
      orgNumber?: string;
      vatNumber?: string;
      contactPersonName?: string;
      contactPersonEmail?: string;
      contactPersonPhone?: string;
      isBuyer?: boolean;
      isSeller?: boolean;
    }
  ) =>
    unwrap(api.put<ApiResponse<DealerPortalCustomer>>(`${BASE}/customers/${id}`, body)),

  updateCustomerVehicleConnections: (customerId: string, vehicleIds: string[]) =>
    unwrap(
      api.put<ApiResponse<DealerPortalCustomer>>(`${BASE}/customers/${customerId}/vehicle-connections`, {
        vehicleIds,
      })
    ),

  deleteCustomer: (id: string) =>
    api.delete<{ success: boolean }>(`${BASE}/customers/${id}`).then((r) => (r as any).data ?? r),

  addCustomerNote: (customerId: string, content: string) =>
    unwrap(
      api.post<ApiResponse<DealerPortalCustomerNote>>(`${BASE}/customers/${customerId}/notes`, {
        content,
      })
    ),

  createCustomerDocument: (customerId: string) =>
    unwrap(
      api.post<ApiResponse<{ id: string }>>(`${BASE}/customers/${customerId}/documents`)
    ),

  /**
   * Full upload flow: create document record, get presigned URL, upload to S3, complete media.
   * Use this from hooks so UI never calls MediaService directly for dealer portal docs.
   */
  async uploadCustomerDocument(customerId: string, file: File): Promise<void> {
    const { id: docId } = await this.createCustomerDocument(customerId);
    const { uploadUrl, s3Key } = await MediaService.requestPresignedUploadUrl(
      "dealerPortalCustomer",
      customerId,
      { originalName: file.name, mimeType: file.type, sizeBytes: file.size },
      "documents"
    );
    await MediaService.uploadFileToS3(uploadUrl, file);
    const media = await MediaService.createMediaAfterUpload(
      s3Key,
      { originalName: file.name, mimeType: file.type, sizeBytes: file.size },
      { dealerPortalCustomerDocumentId: docId }
    );
    await MediaService.updateMediaStatus(media.id, "UPLOADED");
  },

  deleteCustomerDocument: (customerId: string, mediaId: string) =>
    api
      .delete<{ success: boolean }>(`${BASE}/customers/${customerId}/documents/${mediaId}`)
      .then((r) => (r as { data?: unknown }).data ?? r),

  getVehicles: (search?: string) =>
    unwrap(
      api.get<ApiResponse<DealerPortalVehicle[]>>(
        `${BASE}/vehicles${search != null && search !== "" ? `?search=${encodeURIComponent(search)}` : ""}`
      )
    ),

  getVehiclesPaginated: (
    params: { search?: string; page: number; limit: number; status?: string | string[] | null }
  ): Promise<{ items: DealerPortalVehicle[]; total: number; page: number; limit: number }> => {
    const q = new URLSearchParams();
    if (params.search != null && params.search !== "") q.set("search", params.search);
    q.set("page", String(params.page));
    q.set("limit", String(params.limit));
    if (params.status != null && params.status !== "") {
      const arr = Array.isArray(params.status) ? params.status : [params.status];
      const filtered = arr.filter((s) => s != null && s !== "");
      if (filtered.length > 0) q.set("status", filtered.join(","));
    }
    return unwrap(
      api.get<ApiResponse<{ items: DealerPortalVehicle[]; total: number; page: number; limit: number }>>(
        `${BASE}/vehicles?${q.toString()}`
      )
    );
  },

  createVehicle: (body: {
    regNr: string;
    make: string;
    model: string;
    year: number;
    status?: DealerPortalVehicleStatus | string;
    parkingSpot?: string | null;
    variant?: string | null;
    condition?: string | null;
    mileage?: number | null;
    engine?: string | null;
    transmission?: string | null;
    bodyType?: string | null;
    color?: string | null;
    fuelType?: string | null;
    horsepower?: number | null;
    engineSize?: string | null;
    vin?: string | null;
    equipment?: string[];
  }) =>
    unwrap(api.post<ApiResponse<DealerPortalVehicle>>(`${BASE}/vehicles`, body)),

  getVehicle: (id: string) =>
    unwrap(api.get<ApiResponse<DealerPortalVehicle>>(`${BASE}/vehicles/${id}`)),

  getVehicleCarTests: (vehicleId: string) =>
    unwrap(
      api.get<ApiResponse<DealerPortalVehicleCarTestSummary[]>>(
        `${BASE}/vehicles/${vehicleId}/car-tests`
      )
    ),

  updateVehicle: (
    id: string,
    body: {
      regNr?: string;
      make?: string;
      model?: string;
      year?: number;
      status?: DealerPortalVehicleStatus | string | null;
      parkingSpot?: string | null;
      variant?: string | null;
      condition?: string | null;
      mileage?: number | null;
      engine?: string | null;
      transmission?: string | null;
      bodyType?: string | null;
      color?: string | null;
      fuelType?: string | null;
      horsepower?: number | null;
      engineSize?: string | null;
      vin?: string | null;
      equipment?: string[];
    }
  ) =>
    unwrap(api.put<ApiResponse<DealerPortalVehicle>>(`${BASE}/vehicles/${id}`, body)),

  deleteVehicle: (id: string) =>
    api.delete<{ success: boolean }>(`${BASE}/vehicles/${id}`).then((r) => (r as any).data ?? r),

  createVehicleDocument: (vehicleId: string, imageCategory?: string | null, documentCategory?: string | null) =>
    unwrap(
      api.post<ApiResponse<{ id: string }>>(`${BASE}/vehicles/${vehicleId}/documents`, {
        imageCategory: imageCategory ?? undefined,
        documentCategory: documentCategory ?? undefined,
      })
    ),

  async uploadVehicleDocument(
    vehicleId: string,
    file: File,
    options?: { imageCategory?: string | null; documentCategory?: string | null }
  ): Promise<void> {
    const imageCategory = options?.imageCategory;
    const documentCategory = options?.documentCategory;
    const { id: docId } = await this.createVehicleDocument(vehicleId, imageCategory, documentCategory);
    const isImage = imageCategory != null;
    const subPath = isImage ? "images/fullsize" : "documents";
    const { uploadUrl, s3Key } = await MediaService.requestPresignedUploadUrl(
      "dealerPortalVehicle",
      vehicleId,
      { originalName: file.name, mimeType: file.type, sizeBytes: file.size },
      subPath
    );
    await MediaService.uploadFileToS3(uploadUrl, file);
    const fullSizeMedia = await MediaService.createMediaAfterUpload(
      s3Key,
      { originalName: file.name, mimeType: file.type, sizeBytes: file.size },
      { dealerPortalVehicleDocumentId: docId, mediaSize: isImage ? "FULL_SIZE" : null }
    );
    await MediaService.updateMediaStatus(fullSizeMedia.id, "UPLOADED");

    if (isImage) {
      const previewFile = await compressImageForPreview(file);
      const { uploadUrl: previewUploadUrl, s3Key: previewS3Key } =
        await MediaService.requestPresignedUploadUrl(
          "dealerPortalVehicle",
          vehicleId,
          {
            originalName: file.name,
            mimeType: previewFile.type,
            sizeBytes: previewFile.size,
          },
          "images/preview"
        );
      await MediaService.uploadFileToS3(previewUploadUrl, previewFile);
      const previewMedia = await MediaService.createMediaAfterUpload(
        previewS3Key,
        {
          originalName: file.name,
          mimeType: previewFile.type,
          sizeBytes: previewFile.size,
        },
        { dealerPortalVehicleDocumentId: docId, mediaSize: "PREVIEW" }
      );
      await MediaService.updateMediaStatus(previewMedia.id, "UPLOADED");
    }
  },

  deleteVehicleDocument: (vehicleId: string, mediaId: string) =>
    api
      .delete<{ success: boolean }>(`${BASE}/vehicles/${vehicleId}/documents/${mediaId}`)
      .then((r) => (r as { data?: unknown }).data ?? r),

  updateVehicleDocumentComment: (
    vehicleId: string,
    documentId: string,
    comment: string | null
  ) =>
    unwrap(
      api.patch<ApiResponse<{ id: string; comment: string | null }>>(
        `${BASE}/vehicles/${vehicleId}/documents/${documentId}/comment`,
        { comment }
      )
    ),

  addVehicleServiceDetail: (
    vehicleId: string,
    data: { date: string; mileage: number; serviceType: string; notes?: string | null }
  ) =>
    unwrap(
      api.post<ApiResponse<DealerPortalVehicleServiceDetail>>(
        `${BASE}/vehicles/${vehicleId}/service-details`,
        data
      )
    ),

  removeVehicleServiceDetail: (vehicleId: string, detailId: string) =>
    api
      .delete<{ success: boolean }>(`${BASE}/vehicles/${vehicleId}/service-details/${detailId}`)
      .then((r) => (r as { data?: unknown }).data ?? r),

  updateVehicleServiceDetail: (
    vehicleId: string,
    detailId: string,
    data: { date: string; mileage: number; serviceType: string; notes?: string | null }
  ) =>
    unwrap(
      api.patch<ApiResponse<DealerPortalVehicleServiceDetail>>(
        `${BASE}/vehicles/${vehicleId}/service-details/${detailId}`,
        data
      )
    ),

  getJourneys: () => unwrap(api.get<ApiResponse<DealerPortalCustomerJourney[]>>(`${BASE}/journeys`)),

  getJourneysByStatus: (status: string, page: number, limit: number) =>
    unwrap(
      api.get<ApiResponse<{ items: DealerPortalCustomerJourney[]; total: number; page: number; limit: number }>>(
        `${BASE}/journeys?status=${encodeURIComponent(status)}&page=${page}&limit=${limit}`
      )
    ),

  getJourneyCounts: () =>
    unwrap(api.get<ApiResponse<Record<string, number>>>(`${BASE}/journeys/counts`)),

  getJourney: (id: string) =>
    unwrap(api.get<ApiResponse<DealerPortalCustomerJourney>>(`${BASE}/journeys/${id}`)),

  createJourney: (body: {
    customerId: string;
    vehicleId?: string | null;
    status?: string;
    priority?: string;
    assignedActorId?: string | null;
    followUpNote?: string | null;
    followUpAt?: string | null;
  }) =>
    unwrap(api.post<ApiResponse<DealerPortalCustomerJourney>>(`${BASE}/journeys`, body)),

  updateJourney: (
    id: string,
    body: {
      status?: string;
      priority?: string;
      assignedActorId?: string | null;
      vehicleId?: string | null;
      followUpNote?: string | null;
      followUpAt?: string | null;
      statusChangeReason?: string;
    }
  ) =>
    unwrap(api.put<ApiResponse<DealerPortalCustomerJourney>>(`${BASE}/journeys/${id}`, body)),

  addJourneyComment: (id: string, payload: { content: string; mentions?: string[] }) =>
    unwrap(
      api.post<ApiResponse<DealerPortalCustomerJourney>>(`${BASE}/journeys/${id}/comments`, payload)
    ),

  addVehicleComment: (vehicleId: string, payload: { content: string; mentions?: string[] }) =>
    unwrap(
      api.post<ApiResponse<DealerPortalVehicle>>(`${BASE}/vehicles/${vehicleId}/comments`, payload)
    ),

  deleteJourney: (id: string) =>
    api.delete<{ success: boolean }>(`${BASE}/journeys/${id}`).then((r) => (r as any).data ?? r),
};
